import * as XLSX from 'xlsx-js-style';

export function exportTekniikkatauluToExcel({
  type,
  tableData,
  totalsByType,
  years,
  filename
}) {
  const currentYear = new Date().getFullYear();
  const colCount = 2 + years.length; // label | KL | years...

  const GREEN = '04AA00';
  const LIGHT_GRAY = 'F0F0F0';
  const WHITE = 'FFFFFF';
  const BLACK = '000000';
  const RED = 'BA3B46';
  const YELLOW = 'D0C407';
  const DARK_GREEN = '04AA00';
  const BORDER = 'DDDDDD';

  const rows = [];
  const sectionRows = [];
  const itemRowMeta = [];

  const getKLColor = (kl) => {
    switch (kl) {
      case 'KL1':
      case 'KL2':
        return RED;
      case 'KL3':
        return YELLOW;
      case 'KL4':
      case 'KL5':
        return DARK_GREEN;
      default:
        return BLACK;
    }
  };

  const normalizeValue = (v) => {
    if (v === '' || v === null || v === undefined || v === '0' || v === 0) {
      return '';
    }
    const n = Number(v);
    return Number.isNaN(n) ? '' : n;
  };

  const calcSectionAverageKL = (items = []) => {
    const nums = items
      .map((it) => parseInt((it.kl || '').replace('KL', ''), 10))
      .filter((n) => !Number.isNaN(n));

    if (!nums.length) return '';
    const avg = Math.round(nums.reduce((a, b) => a + b, 0) / nums.length);
    return `KL${avg}`;
  };

  // Row 0: super header
  rows.push([
    '',
    '',
    `Kustannusarvio (x 1000€) Kustannustaso ${currentYear} sis. Alv 25,5%`,
    ...Array(years.length - 1).fill('')
  ]);

  // Row 1: column header
  rows.push([type, '', ...years.map(String)]);

  // Body
  tableData.forEach((section) => {
    const avgKL = calcSectionAverageKL(section.items);

    const sectionRowIndex = rows.length;
    sectionRows.push(sectionRowIndex);

    rows.push([section.name, avgKL, ...Array(years.length).fill('')]);

    section.items.forEach((item) => {
      const label = (item.label || '').trim();
      if (label.length < 1) return;

      const values = (item.values || []).map(normalizeValue);
      const rowIndex = rows.length;

      itemRowMeta.push({
        rowIndex,
        values
      });

      // KL shown only on main section rows, not on item rows
      rows.push([
        label,
        '',
        ...values
      ]);
    });
  });

  // Totals
  const totalRowIdx = rows.length;
  rows.push([
    'YHTEENSÄ',
    '',
    ...totalsByType.map(normalizeValue)
  ]);

  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Merge only the right-side superheader area
  ws['!merges'] = [
    { s: { r: 0, c: 2 }, e: { r: 0, c: colCount - 1 } }
  ];

  ws['!cols'] = [
    { wch: 42 },
    { wch: 7 },
    ...years.map(() => ({ wch: 9 }))
  ];

  ws['!rows'] = rows.map((_, i) => {
    if (i === 0 || i === 1) return { hpt: 20 };
    return { hpt: 18 };
  });

  const baseBorder = {
    top: { style: 'thin', color: { rgb: BORDER } },
    bottom: { style: 'thin', color: { rgb: BORDER } },
    left: { style: 'thin', color: { rgb: BORDER } },
    right: { style: 'thin', color: { rgb: BORDER } }
  };

  const headerGreenStyle = {
    font: { bold: true, color: { rgb: WHITE } },
    fill: { fgColor: { rgb: GREEN } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: baseBorder
  };

  const superHeaderTextStyle = {
    font: { bold: true, color: { rgb: WHITE } },
    fill: { fgColor: { rgb: GREEN } },
    alignment: { horizontal: 'right', vertical: 'center', wrapText: false },
    border: baseBorder
  };

  const sectionLabelStyle = {
    font: { bold: true, color: { rgb: BLACK } },
    fill: { fgColor: { rgb: LIGHT_GRAY } },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
    border: baseBorder
  };

  const sectionKLStyle = (rgb) => ({
    font: { bold: true, color: { rgb } },
    fill: { fgColor: { rgb: LIGHT_GRAY } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: baseBorder
  });

  const sectionFillStyle = {
    fill: { fgColor: { rgb: LIGHT_GRAY } },
    border: baseBorder
  };

  const labelStyle = {
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true, indent: 1 },
    border: baseBorder
  };

  const emptyKLBodyStyle = {
    alignment: { horizontal: 'center', vertical: 'center' },
    border: baseBorder
  };

  const numberStyle = {
    alignment: { horizontal: 'center', vertical: 'center' },
    numFmt: '0',
    border: baseBorder
  };

  const positiveValueStyle = {
    font: { bold: true, color: { rgb: WHITE } },
    fill: { fgColor: { rgb: GREEN } },
    alignment: { horizontal: 'center', vertical: 'center' },
    numFmt: '0',
    border: baseBorder
  };

  const totalsStyle = {
    font: { bold: true, color: { rgb: WHITE } },
    fill: { fgColor: { rgb: GREEN } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: baseBorder
  };

  // Row 0 styling
  for (let c = 0; c < colCount; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (!ws[addr]) continue;
    ws[addr].s = c >= 2 ? superHeaderTextStyle : headerGreenStyle;
  }

  // Row 1 styling
  for (let c = 0; c < colCount; c++) {
    const addr = XLSX.utils.encode_cell({ r: 1, c });
    if (!ws[addr]) continue;
    ws[addr].s = headerGreenStyle;
  }

  // Section rows styling
  sectionRows.forEach((r) => {
    for (let c = 0; c < colCount; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!ws[addr]) continue;

      if (c === 0) {
        ws[addr].s = sectionLabelStyle;
      } else if (c === 1) {
        const avgKL = ws[addr].v || '';
        ws[addr].s = sectionKLStyle(getKLColor(avgKL));
      } else {
        ws[addr].s = sectionFillStyle;
      }
    }
  });

  // Item rows styling
  itemRowMeta.forEach(({ rowIndex, values }) => {
    const labelAddr = XLSX.utils.encode_cell({ r: rowIndex, c: 0 });
    if (ws[labelAddr]) ws[labelAddr].s = labelStyle;

    const klAddr = XLSX.utils.encode_cell({ r: rowIndex, c: 1 });
    if (ws[klAddr]) ws[klAddr].s = emptyKLBodyStyle;

    values.forEach((v, idx) => {
      const c = idx + 2;
      const addr = XLSX.utils.encode_cell({ r: rowIndex, c });
      if (!ws[addr]) return;

      ws[addr].s =
        v !== '' && Number(v) > 0
          ? positiveValueStyle
          : numberStyle;
    });
  });

  // Totals row styling
  for (let c = 0; c < colCount; c++) {
    const addr = XLSX.utils.encode_cell({ r: totalRowIdx, c });
    if (ws[addr]) ws[addr].s = totalsStyle;
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, type.substring(0, 31));

  const outFilename = filename || `PTS_${type}_${currentYear}.xlsx`;
  XLSX.writeFile(wb, outFilename);
}